import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useInterventionCreation } from '../../context/interventionCreationContext';
import { PdsIdsPayload, TransitionResult } from '../Assignment/assignment';
import { apiErrorMessage } from './apiError';
import { buildSendToAssignmentUrl } from './qualification';

export type SendToAssignmentState = {
  sendToAssignment: () => void;
  isSending: boolean;
  hasFailed: boolean;
  failureReason: string | null;
  dismissError: () => void;
};

/**
 * Envoie les demandes cochées à l'étape Affectation sans passer par le formulaire de
 * qualification : un raccourci pour celles qui sont déjà complètes, typiquement arrivées
 * pré-qualifiées par import.
 *
 * Le bilan part dans le contexte, et non dans un état local : le bandeau qui l'affiche
 * vit dans le panneau, au-dessus de la table d'où part l'envoi. Ses trois voisins —
 * import, qualification, suppression — sont portés de la même façon, et comme eux il est
 * effacé au changement d'étape.
 */
export function useSendToAssignment(): SendToAssignmentState {
  const { state, dispatch } = useInterventionCreation();
  const queryClient = useQueryClient();

  const mutation = useMutation<TransitionResult, unknown, void>({
    mutationFn: async () => {
      if (state.clientId === null) {
        throw new Error('Aucun client sélectionné');
      }

      const payload: PdsIdsPayload = { pds_ids: state.selectedRequestIds };
      const response = await axios.post(buildSendToAssignmentUrl(state.clientId), payload);

      return response.data;
    },
    onSuccess: (result) => {
      // La liste qu'on quitte et celle qu'on alimente : sans la seconde, le compteur
      // « à affecter » du stepper resterait périmé.
      queryClient.invalidateQueries(['exocet-pds-to-qualify']);
      queryClient.invalidateQueries(['exocet-pds-to-simple-assign']);

      dispatch({
        type: 'reportSendToAssignment',
        report: {
          succeededCount: result.success_count,
          // `failed_ids` est toujours présent, `errors` non : la v1 plantait ici.
          failedCount: result.failed_ids.length,
          failures: result.errors ?? []
        }
      });

      // Les demandes abouties quittent la liste ; les refusées restent cochées. C'est
      // plus qu'une commodité sur cet endpoint : l'API ne détaille pas ses refus, et la
      // case cochée est alors le seul repère de la ligne à corriger.
      dispatch({ type: 'setSelectedRequests', ids: result.failed_ids });
    }
  });

  return {
    // Enveloppé, et non `mutation.mutate` nu : branché sur `onClick`, il recevrait
    // l'événement de souris comme variables de mutation.
    sendToAssignment: () => mutation.mutate(),
    isSending: mutation.isLoading,
    hasFailed: mutation.isError,
    failureReason: apiErrorMessage(mutation.error),
    dismissError: () => mutation.reset()
  };
}
